import * as Sentry from "@sentry/react";
import axios from "axios";
import { getLoggedinUserId } from "services/auth";
import {
  isConnectivityOffline,
  isNetworkTroubleSuspected
} from "services/connectivity-state";
import cumulativeErrorMessageUtil from "./cumulative-error-message-util";
import {
  ErrorMessages,
  ErrorTypes,
  SentryErrorSources,
  SentryTags
} from "../../constants";

export interface HandleErrorOptions {
  error: any;
  showError?: boolean;
  errorSource?: (typeof SentryErrorSources)[keyof typeof SentryErrorSources];
}

/**
 * Auth-expiry errors that arrive via the network layer (a 401 / JWTExpired
 * Hasura close, rather than a graphQL `invalid-jwt` extension) are expected,
 * handled-by-refresh conditions during token rotation/teardown — not real
 * errors. Treat them like the existing graphQL `invalid-jwt` skip.
 */
function isNetworkAuthExpiry(error: any): boolean {
  const ne = error?.networkError;
  if (!ne) return false;
  if (ne.statusCode === 401) return true;
  // The trailing `?? ""` fallback is unreachable: `ne` is guaranteed truthy by
  // the `if (!ne) return false` guard above, so `ne.message ?? ne` is always
  // truthy and String() never receives "". (The .includes("JWTExpired") check
  // itself is exercised by dedicated tests.)
  /* v8 ignore next */
  if (String(ne.message ?? ne ?? "").includes("JWTExpired")) return true;
  return Boolean(
    ne.result?.errors?.some?.((e: any) => e?.extensions?.code === "invalid-jwt")
  );
}

export default function ErrorManager() {
  const errorMapper = [
    // Network failure while CONNECTED — the flaky-network message. The
    // offline case never reaches this mapper: while offline every request
    // fails for the same one reason and the sticky connectivity notice
    // already states it, so per-request toasts are suppressed wholesale.
    {
      matchError: (errorStr: string) => {
        return ErrorTypes.netWorkError(errorStr);
      },
      errorMsg: ErrorMessages.NetworkError.internetError
    },
    {
      matchError: (errorStr: string) => {
        return ErrorTypes.fieldNotFound(errorStr);
      },
      errorMsg: ErrorMessages.FieldNotFoundMessage
    }
  ];

  const handleError = (options: HandleErrorOptions) => {
    const { error, showError = true, errorSource = "unknown" } = options;

    // Canceled (session teardown) or network-level auth expiry → fully silent:
    // no Sentry, no toast. These are expected during logout / token rotation.
    // Apollo wraps a canceled auth-header resolution as networkError, so check
    // both layers.
    if (
      axios.isCancel(error) ||
      axios.isCancel(error?.networkError) ||
      isNetworkAuthExpiry(error)
    ) {
      return { success: false, error, errorMsg: null };
    }

    // invalid-jwt: token Hasura rejected; invalid-headers: blank Authorization
    // from a request racing teardown ("Malformed Authorization header"). Both
    // are session-expiry symptoms, not incidents.
    if (
      error?.graphQLErrors?.some(
        (e: any) =>
          e?.extensions?.code === "invalid-jwt" ||
          e?.extensions?.code === "invalid-headers"
      )
    ) {
      return {
        success: false,
        error,
        errorMsg: "Session expired"
      };
    }

    if (!error) {
      Sentry.captureMessage("handleError called with undefined/null error");
      return null;
    }

    const errorStr = error.response?.data?.error || error?.message;
    let errorMsg = null;

    // A network-shaped failure while the heartbeat is still deciding must not
    // flash a popup the next second may retract — either it confirms offline
    // (sticky notice takes over) or it recovers (nothing needed).
    const undecidedNetworkBlip =
      ErrorTypes.netWorkError(errorStr) && isNetworkTroubleSuspected();

    // Offline covers BOTH hard offline (navigator) and heartbeat-derived
    // offline (Wi-Fi off behind virtual adapters) — either way the sticky
    // connectivity notice is the single voice; per-request toasts stay quiet.
    if (showError && !isConnectivityOffline() && !undecidedNetworkBlip) {
      errorMapper.every((errorTypeObj) => {
        if (errorTypeObj.matchError(errorStr)) {
          errorMsg = errorTypeObj.errorMsg;
          return false;
        }
        return true;
      });

      if (!errorMsg) {
        errorMsg = ErrorMessages.DefaultFailureMessage;
      }

      cumulativeErrorMessageUtil.showError(errorMsg);
    }

    Sentry.captureException(error, {
      tags: {
        [SentryTags.error_source]: errorSource,
        [SentryTags.user_id]: getLoggedinUserId() || "unknown"
      }
    });
    return errorStr;
  };

  return { handleError };
}
